import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Route,
  Switch,
  useRouteMatch,
  useLocation,
} from 'react-router-dom';
import queryString from 'query-string';

import {
  closeSaveQuoteModalConfirmation,
  forgetPasswordVisibleChanged,
  getQuoteSiteAppearances,
  loadMultiModalData,
  newCustomerChanged,
  saveSelectedAddPetModal,
  saveSelectedEditPetModal,
  validateEmployerId,
} from '../../../actions';

import { YourInfoContent } from '../yourInfo/YourInfoContent';
import { MODAL_RELATION_LIST, QuotingSteps } from '../../../constants';
import LiveVetInfoScreen from '../../liveVetInfo/LiveVetInfoScreen';
import LiveVetContextRoot from '../../contexts/LiveVetContext';
import ModalContainer from '../../common/ModalContainer';
import { ModalAddPet } from '../../common/modalAddPet/ModalAddPet';
import ModalRemovePet from '../ModalRemovePet';
import MultiModalInfo from '../../common/MultiModalInfo';
import { SaveYourQuoteModal } from '../SaveYourQuoteModal';
import SidebarSaveQuoteModalConfirmation
  from '../SidebarSaveQuoteModalConfirmation';
import { QuoteContent } from '../QuoteContent';
import { FigoLoadingOverlay } from '../../common/FigoLoadingOverlay';
import {
  createEffectiveDate,
  forceFocusElement,
  formatParameters,
} from '../../../util';
import { DuplicatedPurchasesModal } from '../purchase/duplicatedPurchasesModal';

import './css/QuotingHeader.css';
import './css/ZoomHandler.css';
import { Header } from './Header';
import {
  useCustomerVerification,
  usePrevious,
} from '../../../hooks';
import { dataLayerViewItem } from '../../../gtmDataLayer/dataLayerViewItem';
import MultiModalAsCollapsible from '../../common/MultiModalAsCollapsible';
import { useZoomDetectorOnSite } from '../../../hooks/useZoomDetector';
import { useQuoteIdTracker } from '../../../hooks/useQuoteIdTracker';
import { NEW_CUSTOMER_EMAIL_CHANGED } from '../../../actions/types';
import {
  CustomerVerificationModal,
} from '../../common/otp/CustomerVerificationModal';
import ModalCustomerLogin from '../../common/ModalCustomerLogin';
import ModalForgetPassword from '../../common/ModalForgetPassword';

const {
  NEED_TO_KNOW_MODAL_ID,
  PET_CO_PAY_MODAL_ID,
  TERMS_AND_COND_MODAL_ID,
  WELLNESS_MODAL_ID,
} = MODAL_RELATION_LIST;

const QuotingHeader = () => {
  const match = useRouteMatch();
  const dispatch = useDispatch();
  const location = useLocation();
  const parametersRaw = queryString.parse(location.search);
  const parameters = formatParameters(parametersRaw);
  const store = useSelector(({ quoting }) => quoting);
  const [showModalLogin, setShowModalLogin] = useState(false);
  useZoomDetectorOnSite();
  useQuoteIdTracker();
  const customerVerification = useCustomerVerification();
  const {
    addPetModalVisible,
    currentQuoteStep,
    customerZipCode,
    editPetModalVisible,
    employerIdValidated,
    isLoading,
    newCustomer,
    multiModalData,
    multiModalUserSelection,
    removePetModalVisible,
    saveQuote,
    loggingWithoutModal,
    stripe: { stripePaymentMethodLoading },
  } = store;

  const {
    isCustomer,
    loadingLogin,
    nopCommerceUser,
  } = store.sessionInformation;

  const {
    needToKnowIdSelected,
    needToKnowModalVisible,
    petCoPayIdSelected,
    petCoPayModalVisible,
    termsAndCondIdSelected,
    termsAndCondModalVisible,
    wellnessIdSelected,
    wellnessModalVisible,
    shouldUseCollapsible,
  } = multiModalUserSelection || {};
  const { savedQuoteModalVisible } = saveQuote || {};
  const isSecondStep = currentQuoteStep === QuotingSteps.YourInfo.value;

  const styles = useMemo(() => {
    if (Object.entries(parametersRaw).length || isSecondStep) {
      return {
        bodyStyle: 'App-body-quote',
      };
    }

    return {
      bodyStyle: '',
    };
  }, [isSecondStep, parametersRaw]);

  const showLoadingOverlay = useMemo(() => isLoading
    || store.isLoadingNewAddress
    || store.purchase.loading
    || store.rateSummary.loading
    || store.sessionInformation.isLoading
    || stripePaymentMethodLoading, [
    isLoading,
    store.isLoadingNewAddress,
    store.purchase.loading,
    store.rateSummary.loading,
    store.sessionInformation.isLoading,
    stripePaymentMethodLoading,
  ]);

  useEffect(() => {
    if (parameters.empGuid && !employerIdValidated) {
      dispatch(validateEmployerId(parameters.empGuid));
      dispatch(getQuoteSiteAppearances(parameters.empGuid));
    }
  }, [employerIdValidated, parameters.empGuid, dispatch]);

  const handleCloseModalAddPet = useCallback(() => {
    dispatch(saveSelectedEditPetModal(false));
    dispatch(saveSelectedAddPetModal(false));
    const { petQuoteSelected, data: { ebPetQuoteResponseList } } = store;
    const currentQuote = (ebPetQuoteResponseList || [])
      .find((quote) => quote.petQuoteId === petQuoteSelected);
    forceFocusElement(`pet-${currentQuote.petQuoteId}`);
  }, [dispatch, store]);

  const handleOnClickCloseConfirmationModal = useCallback(() => {
    dispatch(closeSaveQuoteModalConfirmation());
  }, [dispatch]);

  const prevEffectiveDateCustom = usePrevious(store.effectiveDateCustom);

  useEffect(() => {
    const isEffectiveDateCustomChanged = store.effectiveDateCustom
      !== prevEffectiveDateCustom;
    if ((!customerZipCode || customerZipCode === multiModalData?.ZipCode)
      && !isEffectiveDateCustomChanged) {
      return;
    }

    dispatch(loadMultiModalData({
      effectiveDate: store.effectiveDateCustom || createEffectiveDate(),
      isEBProduct: true,
      zipCode: customerZipCode,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerZipCode, dispatch, multiModalData, store.effectiveDateCustom]);

  useEffect(() => {
    if (newCustomer.email && isCustomer && !nopCommerceUser.LoginSuccess
      && (isSecondStep || (!isSecondStep && !loggingWithoutModal))) {
      setShowModalLogin(true);
      dispatch(forgetPasswordVisibleChanged({ visible: false }));
    }

    if (nopCommerceUser.LoginSuccess) {
      setShowModalLogin(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isCustomer, newCustomer.email, nopCommerceUser]);

  // GTM
  useEffect(() => {
    dataLayerViewItem({
      pathname: location.pathname,
      petIndex: store.petQuoteSelected,
    });
  }, [location.pathname, store.petQuoteSelected]);

  const onDismissLoginModal = useCallback(() => {
    if (!loadingLogin) {
      dispatch(newCustomerChanged({
        type:
          NEW_CUSTOMER_EMAIL_CHANGED,
        value: '',
      }));
      setShowModalLogin(false);
    }
  }, [dispatch, loadingLogin]);

  const handleForgetPasswordClick = useCallback(() => {
    if (!loadingLogin) {
      setShowModalLogin(false);
      dispatch(forgetPasswordVisibleChanged({ visible: true }));
    }
  }, [dispatch, loadingLogin]);

  const handleCloseForgotPassword = useCallback(() => {
    setShowModalLogin(true);
  }, []);

  /** Latest price before pet info update */
  const priceToCompare = useRef(null);
  const registerPriceToCompare = useCallback((price) => {
    priceToCompare.current = price;
  }, []);

  return (
    <>
      <Header />

      <main className={`App-body ${styles.bodyStyle}`}>
        <Switch>
          <Route exact path={match.path}>
            <QuoteContent />
          </Route>

          <Route path={`${match.path}/yourInfo/:scrollToTop?`}>
            <YourInfoContent priceToCompare={priceToCompare.current} />
          </Route>

          <Route path={`${match.path}/liveVetInfo/:scrollToTop?`}>
            <LiveVetContextRoot.Provider>
              <LiveVetInfoScreen />
            </LiveVetContextRoot.Provider>
          </Route>
        </Switch>
      </main>

      <DuplicatedPurchasesModal />

      <ModalContainer
        cancelable
        handleOutClick={handleCloseModalAddPet}
        idPrefix="addAnotherPetModal"
        keepInDOM
        show={addPetModalVisible || editPetModalVisible}
      >
        <ModalAddPet
          handleCloseModalAddPet={handleCloseModalAddPet}
          isEditing={editPetModalVisible}
          isSecondStep={isSecondStep}
          registerPriceToCompare={registerPriceToCompare}
          show={addPetModalVisible || editPetModalVisible}
        />
      </ModalContainer>

      <ModalContainer cancelable show={removePetModalVisible}>
        <ModalRemovePet isSecondStep={isSecondStep} />
      </ModalContainer>

      <ModalContainer
        cancelable={customerVerification}
        handleOutClick={onDismissLoginModal}
        show={!showLoadingOverlay && showModalLogin}
      >
        {customerVerification
          ? (
            <CustomerVerificationModal
              onDismiss={onDismissLoginModal}
            />
          ) : (
            <ModalCustomerLogin
              emailUser={newCustomer.email}
              onDismiss={onDismissLoginModal}
              onForgotPassword={handleForgetPasswordClick}
            />
          )}
      </ModalContainer>

      {!customerVerification && (
        <ModalForgetPassword
          onModalClosed={handleCloseForgotPassword}
        />
      )}

      <MultiModalInfo
        id="seeWhatWeCover"
        keepInDOM
        menuSelected={needToKnowIdSelected}
        modalId={NEED_TO_KNOW_MODAL_ID}
        show={!shouldUseCollapsible && needToKnowModalVisible}
      />

      <MultiModalAsCollapsible
        id="seeWhatWeCoverCollapsible"
        keepInDOM
        modalId={MODAL_RELATION_LIST.NEED_TO_KNOW_MODAL_ID}
        show={shouldUseCollapsible && needToKnowModalVisible}
      />

      <MultiModalInfo
        id="termsAndCondition"
        keepInDOM
        menuSelected={termsAndCondIdSelected}
        modalId={TERMS_AND_COND_MODAL_ID}
        show={termsAndCondModalVisible}
      />

      <MultiModalInfo
        id="powerUp70428" // wellness
        keepInDOM
        menuSelected={wellnessIdSelected}
        modalId={WELLNESS_MODAL_ID}
        show={wellnessModalVisible}
      />

      {/* FPA-11163 */}
      <MultiModalInfo
        id="petCopayContent"
        menuSelected={petCoPayIdSelected}
        modalId={PET_CO_PAY_MODAL_ID}
        show={petCoPayModalVisible}
      />

      <MultiModalInfo
        id="powerUp70422"// vet exam fees
        keepInDOM
        menuSelected={-1}
        show={store.multiModalCustomData}
      />

      <SaveYourQuoteModal />

      <SidebarSaveQuoteModalConfirmation
        handleOnClick={handleOnClickCloseConfirmationModal}
        show={savedQuoteModalVisible}
      />

      <FigoLoadingOverlay visible={showLoadingOverlay} />
    </>
  );
};

export { QuotingHeader };
